import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { hubApi, PLAN_PRICING, STATUS_STYLE, BILLING_PERIOD_LABEL, calcFinalAmount } from "../api.js";

const fmt = d => d ? new Date(d).toLocaleDateString("ro-RO", { day: "numeric", month: "short" }) : "—";

export default function DashboardPage() {
  const nav = useNavigate();
  const [restaurants, setRestaurants] = useState([]);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterPlan, setFilterPlan] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    hubApi.getAll()
      .then(({ data }) => setRestaurants(data.restaurants || []))
      .catch(() => toast.error("Nu s-au putut încărca restaurantele."))
      .finally(() => setLoading(false));
  }, []);

  const handleToggle = async (e, id) => {
    e.stopPropagation();
    try {
      const { data } = await hubApi.toggle(id);
      if (data.success) setRestaurants(r => r.map(x => x._id === id ? { ...x, isActive: data.isActive } : x));
    } catch { toast.error("Eroare."); }
  };

  const list = Array.isArray(restaurants) ? restaurants : [];

  // Metrici
  const active = list.filter(r => r.isActive);
  const mrr = active.reduce((sum, r) => {
    const base = calcFinalAmount(r.plan, r.billingPeriod || "monthly", r.discountType, r.discountValue);
    const monthly = r.billingPeriod === "yearly" ? base / 12 : r.billingPeriod === "quarterly" ? base / 3 : base;
    return sum + monthly;
  }, 0);
  const arr = mrr * 12;
  const overdue = list.filter(r => r.billingStatus === "overdue");
  const upcoming = list.filter(r => {
    if (!r.currentDueDate || r.billingStatus === "paid") return false;
    const days = (new Date(r.currentDueDate) - new Date()) / 86400000;
    return days >= 0 && days <= 30;
  }).sort((a, b) => new Date(a.currentDueDate) - new Date(b.currentDueDate));

  const filtered = list.filter(r => {
    const matchSearch = r.name.toLowerCase().includes(search.toLowerCase()) || r.slug.toLowerCase().includes(search.toLowerCase());
    const matchStatus = filterStatus === "all" || r.billingStatus === filterStatus || (filterStatus === "active" && r.isActive) || (filterStatus === "inactive" && !r.isActive);
    const matchPlan = filterPlan === "all" || String(r.plan) === filterPlan;
    return matchSearch && matchStatus && matchPlan;
  });

  return (
    <div style={{ padding: "36px 44px", maxWidth: 1100 }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 32 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: "#0f172a", margin: "0 0 3px", letterSpacing: "-0.4px" }}>Dashboard</h1>
          <p style={{ color: "#94a3b8", fontSize: 13, margin: 0 }}>{new Date().toLocaleDateString("ro-RO", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}</p>
        </div>
        <button onClick={() => nav("/restaurants/new")}
          style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 20px", borderRadius: 9, border: "none", background: "#6366f1", color: "#fff", fontWeight: 700, fontSize: 13, cursor: "pointer", boxShadow: "0 2px 8px rgba(99,102,241,0.3)" }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          Adaugă restaurant
        </button>
      </div>

      {/* KPI cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 14, marginBottom: 24 }}>
        <KpiCard label="MRR" value={`€${mrr.toLocaleString("ro-RO", { maximumFractionDigits: 0 })}`} sub="Venit lunar recurent" color="#6366f1" icon="💰" />
        <KpiCard label="ARR" value={`€${arr.toLocaleString("ro-RO", { maximumFractionDigits: 0 })}`} sub="Venit anual recurent" color="#7c3aed" icon="📈" />
        <KpiCard label="Restaurante active" value={active.length} sub={`din ${list.length} total`} color="#16a34a" icon="🏪" />
        <KpiCard label="Restanțe" value={overdue.length} sub="plăți neachitate" color={overdue.length > 0 ? "#e11d48" : "#94a3b8"} icon="⚠️" />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 320px", gap: 20, marginBottom: 24 }}>
        {/* Plan breakdown */}
        <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #e2e8f0", padding: "20px 22px" }}>
          <p style={sectionLabelSt}>Distribuție planuri</p>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {[1, 2, 3].map(pid => {
              const p = PLAN_PRICING[pid];
              const count = list.filter(r => r.plan === pid && r.isActive).length;
              const pct = active.length ? Math.round(count / active.length * 100) : 0;
              return (
                <div key={pid}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: "#374151" }}>{p.name}</span>
                    <span style={{ fontSize: 13, color: "#64748b" }}>{count} restaurante · €{(count * p.monthly).toLocaleString()}/lună</span>
                  </div>
                  <div style={{ height: 6, borderRadius: 99, background: "#f1f5f9", overflow: "hidden" }}>
                    <div style={{ height: "100%", width: `${pct}%`, background: p.color, borderRadius: 99, transition: "width 0.6s ease" }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Upcoming payments */}
        <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #e2e8f0", padding: "20px 22px" }}>
          <p style={sectionLabelSt}>Plăți scadente (30 zile)</p>
          {upcoming.length === 0 ? (
            <div style={{ textAlign: "center", padding: "16px 0", color: "#94a3b8", fontSize: 13 }}>✅ Nicio plată scadentă</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {upcoming.slice(0, 5).map(r => {
                const days = Math.ceil((new Date(r.currentDueDate) - new Date()) / 86400000);
                return (
                  <div key={r._id} onClick={() => nav(`/restaurants/${r._id}`)}
                    style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "9px 10px", borderRadius: 8, background: days <= 7 ? "#fff1f2" : "#f8fafc", cursor: "pointer" }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>{r.name}</div>
                      <div style={{ fontSize: 11, color: "#94a3b8" }}>{fmt(r.currentDueDate)}</div>
                    </div>
                    <span style={{ fontSize: 11, fontWeight: 700, color: days <= 7 ? "#e11d48" : "#d97706" }}>{days}z</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Filters + table */}
      <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #e2e8f0", overflow: "hidden" }}>
        {/* Toolbar */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "16px 20px", borderBottom: "1px solid #f1f5f9", flexWrap: "wrap" }}>
          <div style={{ position: "relative", flex: 1, minWidth: 200 }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)" }}><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Caută restaurant..."
              style={{ width: "100%", padding: "8px 12px 8px 32px", borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 13, outline: "none", boxSizing: "border-box" }} />
          </div>
          <FilterSelect value={filterStatus} onChange={setFilterStatus} options={[
            { value: "all", label: "Toate statusurile" },
            { value: "active", label: "Activ" },
            { value: "inactive", label: "Inactiv" },
            { value: "paid", label: "Plătit" },
            { value: "pending", label: "În așteptare" },
            { value: "overdue", label: "Restanță" },
          ]} />
          <FilterSelect value={filterPlan} onChange={setFilterPlan} options={[
            { value: "all", label: "Toate planurile" },
            { value: "1", label: "Light" },
            { value: "2", label: "Pro" },
            { value: "3", label: "Enterprise" },
          ]} />
          <span style={{ fontSize: 12, color: "#94a3b8", marginLeft: "auto" }}>{filtered.length} rezultate</span>
        </div>

        {/* Table header */}
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr 80px 40px", padding: "10px 20px", background: "#f8fafc", borderBottom: "1px solid #f1f5f9" }}>
          {["Restaurant", "Plan", "Sumă curentă", "Status", "Scadență", ""].map(h => (
            <span key={h} style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.5px" }}>{h}</span>
          ))}
        </div>

        {loading ? (
          <div style={{ padding: 48, textAlign: "center", color: "#94a3b8" }}>Se încarcă...</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 48, textAlign: "center", color: "#94a3b8" }}>
            <div style={{ fontSize: 28, marginBottom: 8 }}>🔍</div>
            <p style={{ margin: 0, fontSize: 14 }}>Niciun restaurant găsit</p>
          </div>
        ) : filtered.map((r, i) => {
          const p = PLAN_PRICING[r.plan] || PLAN_PRICING[1];
          const ss = STATUS_STYLE[r.billingStatus] || STATUS_STYLE.pending;
          const final = calcFinalAmount(r.plan, r.billingPeriod || "monthly", r.discountType, r.discountValue);
          const currency = r.currentCurrency || "EUR";
          return (
            <div key={r._id} onClick={() => nav(`/restaurants/${r._id}`)}
              style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr 80px 40px", alignItems: "center", padding: "14px 20px", borderTop: i === 0 ? "none" : "1px solid #f8fafc", cursor: "pointer", transition: "background 0.1s" }}
              onMouseEnter={e => e.currentTarget.style.background = "#fafafa"}
              onMouseLeave={e => e.currentTarget.style.background = "transparent"}>

              {/* Name */}
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ width: 36, height: 36, borderRadius: 9, background: `linear-gradient(135deg,${p.bg},${p.border})`, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 14, color: p.color, flexShrink: 0 }}>
                  {r.name.charAt(0).toUpperCase()}
                </div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a", display: "flex", alignItems: "center", gap: 6 }}>
                    {r.name}
                    {!r.isActive && <span style={{ fontSize: 9, background: "#f1f5f9", color: "#94a3b8", padding: "1px 6px", borderRadius: 99, fontWeight: 700, letterSpacing: "0.5px" }}>INACTIV</span>}
                    {r.discountType !== "none" && r.discountValue > 0 && (
                      <span style={{ fontSize: 9, background: "#fef9c3", color: "#854d0e", padding: "1px 6px", borderRadius: 99, fontWeight: 700 }}>
                        {r.discountType === "percent" ? `-${r.discountValue}%` : `-€${r.discountValue}`}
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 11, color: "#94a3b8" }}>{r.slug}.orderly.ro</div>
                </div>
              </div>

              {/* Plan */}
              <span style={{ fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 99, background: p.bg, color: p.color, border: `1px solid ${p.border}`, display: "inline-block" }}>
                {p.name}
              </span>

              {/* Amount */}
              <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>
                {r.currentAmount ? `${r.currentAmount} ${currency}` : `€${final}`}
                <span style={{ fontSize: 11, color: "#94a3b8", fontWeight: 400, marginLeft: 3 }}>/{BILLING_PERIOD_LABEL[r.billingPeriod || "monthly"]?.toLowerCase()}</span>
              </span>

              {/* Status */}
              <span style={{ fontSize: 11, fontWeight: 600, padding: "3px 10px", borderRadius: 99, background: ss.bg, color: ss.color, border: `1px solid ${ss.border}`, display: "inline-block" }}>
                {ss.label}
              </span>

              {/* Due date */}
              <span style={{ fontSize: 12, color: "#64748b" }}>{fmt(r.currentDueDate)}</span>

              {/* Toggle */}
              <button onClick={e => handleToggle(e, r._id)}
                style={{ width: 36, height: 20, borderRadius: 99, border: "none", cursor: "pointer", position: "relative", background: r.isActive ? "#6366f1" : "#e2e8f0", transition: "background 0.2s", flexShrink: 0 }}>
                <span style={{ position: "absolute", top: 2, left: r.isActive ? "calc(100% - 18px)" : 2, width: 16, height: 16, borderRadius: "50%", background: "#fff", transition: "left 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.2)" }} />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function KpiCard({ label, value, sub, color, icon }) {
  return (
    <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #e2e8f0", padding: "20px 22px", boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <p style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.8px", margin: "0 0 8px" }}>{label}</p>
          <div style={{ fontSize: 26, fontWeight: 800, color, lineHeight: 1, letterSpacing: "-0.5px" }}>{value}</div>
          <p style={{ fontSize: 12, color: "#94a3b8", margin: "6px 0 0" }}>{sub}</p>
        </div>
        <span style={{ fontSize: 22 }}>{icon}</span>
      </div>
    </div>
  );
}

function FilterSelect({ value, onChange, options }) {
  return (
    <select value={value} onChange={e => onChange(e.target.value)}
      style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 13, outline: "none", color: "#374151", background: "#fff", cursor: "pointer" }}>
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}

const sectionLabelSt = { fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.8px", margin: "0 0 14px" };
